import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid, Checkbox, Chip, InputAdornment, TextField, Radio,
  Slider,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  MergeType as MergeTypeIcon,
  Search as SearchIcon,
  Language as LanguageIcon,
  CloseOutlined,
} from "@mui/icons-material";
import { useAuth } from "context/AuthContext";
import { FAST_API_BASE_URL, LAMBDA_BASE_URL } from "../../../constants";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";

// ─── All sub-components must be defined OUTSIDE MergeDomainsSection ──────────
// Defining them inside causes React to remount them on every render, which
// destroys the Slider's DOM reference mid-drag → getBoundingClientRect crash.

const PrioritySlider = ({ value, onChange }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      px: 2,
      py: 1.25,
      background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
      borderTop: "1px solid rgba(33,146,151,0.12)",
    }}
  >
    <Typography
      variant="caption"
      fontWeight={700}
      sx={{
        minWidth: 44,
        color: "#219297",
        letterSpacing: "0.05em",
        fontSize: "11px",
        textTransform: "uppercase",
      }}
    >
      Priority
    </Typography>

    <Slider
      value={value}
      onChange={(_, v) => onChange(v)}
      min={1}
      max={99}
      step={1}
      size="small"
      sx={{
        flex: 1,
        color: "#219297",
        "& .MuiSlider-thumb": {
          width: 16,
          height: 16,
          backgroundColor: "#219297",
          border: "2px solid #fff",
          boxShadow: "0 0 0 3px rgba(33,146,151,0.25)",
          transition: "box-shadow 0.15s ease",
          "&:hover": { boxShadow: "0 0 0 5px rgba(33,146,151,0.2)" },
          "&.Mui-active": { boxShadow: "0 0 0 6px rgba(33,146,151,0.25)" },
        },
        "& .MuiSlider-track": {
          backgroundColor: "#219297",
          border: "none",
          height: 4,
          borderRadius: 2,
        },
        "& .MuiSlider-rail": {
          backgroundColor: "rgba(33,146,151,0.15)",
          height: 4,
          borderRadius: 2,
        },
      }}
    />

    <Box
      sx={{
        minWidth: 38,
        textAlign: "center",
        background: "linear-gradient(135deg, #219297 0%, #1a7579 100%)",
        borderRadius: "7px",
        px: 1,
        py: 0.4,
        boxShadow: "0 2px 6px rgba(33,146,151,0.3)",
      }}
    >
      <Typography
        variant="caption"
        fontWeight={800}
        color="#fff"
        fontSize="12px"
        sx={{ letterSpacing: "0.02em" }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

const DomainListItem = ({ domain, isSelected, onClick, isDisabled, showCheckbox }) => (
  <Box
    onClick={!isDisabled ? onClick : undefined}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 1.5,
      py: 1,
      cursor: isDisabled ? "not-allowed" : "pointer",
      borderRadius: "8px",
      mb: 0.5,
      opacity: isDisabled ? 0.3 : 1,
      backgroundColor: isSelected ? "rgba(33,146,151,0.06)" : "transparent",
      border: isSelected ? "1px solid rgba(33,146,151,0.35)" : "1px solid transparent",
      transition: "all 0.15s ease",
      "&:hover": !isDisabled
        ? {
            backgroundColor: "rgba(33,146,151,0.04)",
            border: "1px solid rgba(33,146,151,0.25)",
            transform: "translateX(2px)",
          }
        : {},
    }}
  >
    {showCheckbox ? (
      <Checkbox
        checked={isSelected}
        size="small"
        sx={{
          p: 0,
          color: "#ddd",
          "&.Mui-checked": { color: "#219297" },
          "& .MuiSvgIcon-root": { fontSize: 17 },
        }}
      />
    ) : (
      <Radio
        checked={isSelected}
        size="small"
        sx={{
          p: 0,
          color: "#ddd",
          "&.Mui-checked": { color: "#219297" },
          "& .MuiSvgIcon-root": { fontSize: 17 },
        }}
      />
    )}

    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "6px",
        backgroundColor: isSelected ? "rgba(33,146,151,0.1)" : "rgba(0,0,0,0.03)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "background 0.15s",
      }}
    >
      <LanguageIcon
        sx={{
          fontSize: 13,
          color: isSelected ? "#219297" : "#bbb",
          transition: "color 0.15s",
        }}
      />
    </Box>

    <Box sx={{ overflow: "hidden", flex: 1 }}>
      <Typography
        variant="body2"
        fontSize="13px"
        fontWeight={isSelected ? 600 : 400}
        color={isSelected ? "#219297" : "#4a5568"}
        noWrap
        sx={{ letterSpacing: "-0.01em" }}
      >
        {domain.domain_name}
      </Typography>
    </Box>
  </Box>
);

const ListPanel = ({ title, subtitle, search, onSearch, items, renderItem }) => (
  <Box
    sx={{
      border: "1px solid #e8eef0",
      borderRadius: "12px",
      overflow: "hidden",
      backgroundColor: "#fff",
      boxShadow: "0 2px 8px rgba(33,146,151,0.05)",
    }}
  >
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: "1px solid #f0f4f4",
        background: "linear-gradient(135deg, #f8fefe 0%, #f2fafa 100%)",
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Typography
          variant="caption"
          fontWeight={800}
          sx={{
            textTransform: "uppercase",
            letterSpacing: "0.09em",
            fontSize: "10px",
            color: "#219297",
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            height: "1px",
            flex: 1,
            background: "linear-gradient(to right, rgba(33,146,151,0.2), transparent)",
          }}
        />
      </Box>
      <Typography variant="caption" color="#aab8b8" display="block" mt={0.25} fontSize="11px">
        {subtitle}
      </Typography>
    </Box>

    <Box
      sx={{
        px: 1.5,
        py: 1,
        borderBottom: "1px solid #f5f5f5",
        backgroundColor: "#fdfefe",
      }}
    >
      <TextField
        size="small"
        fullWidth
        placeholder="Search domains…"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 14, color: "#b0c4c4" }} />
            </InputAdornment>
          ),
          sx: {
            fontSize: "13px",
            borderRadius: "8px",
            backgroundColor: "#fff",
            "& fieldset": { borderColor: "#eaeaea" },
            "&:hover fieldset": { borderColor: "#219297 !important" },
            "&.Mui-focused fieldset": {
              borderColor: "#219297 !important",
              borderWidth: "1.5px",
            },
          },
        }}
        inputProps={{ style: { fontSize: "13px", padding: "7px 8px" } }}
      />
    </Box>

    <Box
      sx={{
        maxHeight: 240,
        overflowY: "auto",
        px: 1,
        py: 0.75,
        "&::-webkit-scrollbar": { width: 4 },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": { background: "rgba(33,146,151,0.2)", borderRadius: 2 },
      }}
    >
      {items.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
          <LanguageIcon sx={{ color: "#ddd", fontSize: 28 }} />
          <Typography variant="caption" color="#ccc" fontSize="12px">
            No domains found
          </Typography>
        </Box>
      ) : (
        items.map(renderItem)
      )}
    </Box>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────

const MergeDomainsSection = () => {
  const [domains,         setDomains]         = useState([]);
  const [domainsLoading,  setDomainsLoading]  = useState(false);
  const [parentDomain,    setParentDomain]    = useState(null);
  const [childDomains,    setChildDomains]    = useState([]);
  const [parentPriority,  setParentPriority]  = useState(70);
  const [childPriorities, setChildPriorities] = useState({});
  const [leftSearch,      setLeftSearch]      = useState("");
  const [rightSearch,     setRightSearch]     = useState("");
  const [merging,         setMerging]         = useState(false);
  const [mergeError,      setMergeError]      = useState("");
  const [mergeSuccess,    setMergeSuccess]    = useState("");
  const [mergedGroups,    setMergedGroups]    = useState([]);

  const { user } = useAuth();
  const { userId } = useParams();
  const targetUserId = userId || user?.id;

  useEffect(() => { fetchDomains(); }, []);

  const priorityTotal = useMemo(() => {
    if (!parentDomain || childDomains.length === 0) return null;
    const childSum = childDomains.reduce(
      (s, d) => s + (childPriorities[d.domain_token] ?? 0), 0
    );
    return parentPriority + childSum;
  }, [parentDomain, childDomains, parentPriority, childPriorities]);

  const totalIsValid = priorityTotal === 100;

  const evenSplit = (children, parentPri) => {
    const rem = Math.max(0, 100 - parentPri);
    const per = children.length ? Math.max(1, Math.floor(rem / children.length)) : 0;
    return Object.fromEntries(
      children.map((d, i) => [
        d.domain_token,
        i === children.length - 1
          ? Math.max(1, rem - per * (children.length - 1))
          : per,
      ])
    );
  };

  const fetchDomains = async () => {
    setDomainsLoading(true);
    try {
      const res = await axios.get(
        `${FAST_API_BASE_URL}/api/users/${targetUserId}/merged-domain-details`,
        { headers: { "x-api-key": process.env.REACT_APP_API_KEY } }
      );
      if (res.data?.resultCode === "0" && res.data?.result) {
        const { domains = [], merged_groups = [] } = res.data.result;
        setDomains(domains);
        setMergedGroups(merged_groups);
        if (merged_groups.length > 0) {
          const group = merged_groups[0];
          const parentObj = domains.find(d => d.domain_token === group.parent?.domain_token);
          const childObjs = (group.children || [])
            .map(c => domains.find(d => d.domain_token === c.domain_token))
            .filter(Boolean);
          if (parentObj) {
            setParentDomain(parentObj);
            setParentPriority(group.parent.priority ?? 70);
          }
          if (childObjs.length > 0) {
            setChildDomains(childObjs);
            const initPriorities = {};
            (group.children || []).forEach(c => {
              if (c.domain_token) initPriorities[c.domain_token] = c.priority ?? 0;
            });
            setChildPriorities(initPriorities);
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch domains:", err);
      setMergeError("Failed to load domains. Please refresh.");
    } finally {
      setDomainsLoading(false);
    }
  };

  const handleSelectParent = (domain) => {
    if (parentDomain?.domain_token === domain.domain_token) { setParentDomain(null); return; }
    setParentDomain(domain);
    setChildDomains(prev => {
      const next = prev.filter(d => d.domain_token !== domain.domain_token);
      setChildPriorities(() => evenSplit(next, parentPriority));
      return next;
    });
  };

  const handleToggleChild = (domain) => {
    if (parentDomain?.domain_token === domain.domain_token) return;
    setChildDomains(prev => {
      const exists = prev.find(d => d.domain_token === domain.domain_token);
      if (exists) {
        const next = prev.filter(d => d.domain_token !== domain.domain_token);
        setChildPriorities(cp => { const n = { ...cp }; delete n[domain.domain_token]; return n; });
        return next;
      }
      const next = [...prev, domain];
      setChildPriorities(evenSplit(next, parentPriority));
      return next;
    });
  };

  const handleParentPriorityChange = (val) => {
    const v = Number(val);
    setParentPriority(v);
    if (childDomains.length) setChildPriorities(evenSplit(childDomains, v));
  };

  const handleChildPriorityChange = (changedToken, newVal) => {
    setChildPriorities(prev => {
      const rem = 100 - parentPriority;
      const others = childDomains.filter(d => d.domain_token !== changedToken);
      const leftover = Math.max(0, rem - newVal);
      const perOther = others.length ? Math.floor(leftover / others.length) : 0;
      let distributed = 0;
      const next = { ...prev };
      others.forEach((d, i) => {
        const v = i === others.length - 1
          ? Math.max(1, leftover - distributed)
          : Math.max(1, perOther);
        next[d.domain_token] = v;
        distributed += v;
      });
      const othersSum = others.reduce((s, d) => s + next[d.domain_token], 0);
      next[changedToken] = Math.max(1, rem - othersSum);
      return next;
    });
  };

  const handleMerge = async () => {
    if (!parentDomain)        { setMergeError("Please select a parent domain."); return; }
    if (!childDomains.length) { setMergeError("Please select at least one child domain."); return; }
    if (!totalIsValid)        { setMergeError(`Priorities must sum to 100 (currently ${priorityTotal}).`); return; }
    setMerging(true); setMergeError(""); setMergeSuccess("");
    try {
      const payload = {
        user_id: targetUserId,
        parent_domain_token: parentDomain.domain_token,
        priority: parentPriority,
        children: childDomains.map(d => ({
          token: d.domain_token,
          priority: childPriorities[d.domain_token] ?? 0,
        })),
      };
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/merge-website-collections`, payload,
        { headers: { "x-api-key": process.env.REACT_APP_API_KEY } }
      );
      if (res.data?.resultType === "Success" || res.status === 200) {
        setMergeSuccess(`Successfully merged ${childDomains.length} domain(s) under "${parentDomain.domain_name}".`);
        setParentDomain(null); setChildDomains([]); setChildPriorities({}); setParentPriority(70);
        fetchDomains();
      } else {
        setMergeError(res.data?.message || "Merge failed. Please try again.");
      }
    } catch (err) {
      setMergeError(err.response?.data?.message || "Merge failed. Please try again.");
    } finally {
      setMerging(false);
    }
  };

  const filteredLeft  = domains.filter(d => d.domain_name?.toLowerCase().includes(leftSearch.toLowerCase()));
  const filteredRight = domains.filter(d => d.domain_name?.toLowerCase().includes(rightSearch.toLowerCase()));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box
        mt={3}
        sx={{
          border: "1px solid #e2eaea",
          borderRadius: "16px",
          backgroundColor: "#fff",
          overflow: "hidden",
          boxShadow: "0 4px 24px rgba(33,146,151,0.08), 0 1px 4px rgba(0,0,0,0.04)",
        }}
      >
        <Box
          px={3}
          pt={2.5}
          pb={2}
          sx={{
            borderBottom: "1px solid #edf4f4",
            background: "linear-gradient(135deg, #f5fdfd 0%, #edf8f8 50%, #f8fefe 100%)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: -20, right: -20,
              width: 120, height: 120,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(33,146,151,0.08) 0%, transparent 70%)",
              pointerEvents: "none",
            },
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 34, height: 34,
                borderRadius: "10px",
                background: "linear-gradient(135deg, #219297 0%, #1a7579 100%)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 4px 12px rgba(33,146,151,0.3)",
              }}
            >
              <MergeTypeIcon sx={{ color: "#fff", fontSize: 17 }} />
            </Box>
            <Box>
              <Typography fontWeight={700} fontSize="14.5px" color="#1a2e2e" letterSpacing="-0.02em">
                Merge Domains
              </Typography>
              <Typography variant="caption" color="#9db8b8" fontSize="11px">
                Combine multiple domains under one parent domain
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box px={3} py={2.5}>
          {mergeSuccess && (
            <Alert
              severity="success"
              sx={{
                mb: 2, py: 0.75, fontSize: "13px", borderRadius: "10px",
                border: "1px solid rgba(33,146,151,0.2)",
                backgroundColor: "rgba(33,146,151,0.05)",
                color: "#1a5a5a",
                "& .MuiAlert-icon": { color: "#219297" },
              }}
              onClose={() => setMergeSuccess("")}
            >
              {mergeSuccess}
            </Alert>
          )}
          {mergeError && (
            <Alert
              severity="error"
              sx={{ mb: 2, py: 0.75, fontSize: "13px", borderRadius: "10px" }}
              onClose={() => setMergeError("")}
            >
              {mergeError}
            </Alert>
          )}

          {domainsLoading ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={2}>
              <CircularProgress size={24} sx={{ color: "#219297" }} />
              <Typography variant="caption" color="#b0c4c4" fontSize="12px">
                Loading domains…
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={5}>
                  <ListPanel
                    title="Parent Domain"
                    subtitle="Select one domain"
                    search={leftSearch}
                    onSearch={setLeftSearch}
                    items={filteredLeft}
                    renderItem={(domain) => (
                      <DomainListItem
                        key={domain.domain_token}
                        domain={domain}
                        isSelected={parentDomain?.domain_token === domain.domain_token}
                        onClick={() => handleSelectParent(domain)}
                        isDisabled={false}
                        showCheckbox={false}
                      />
                    )}
                  />
                  {parentDomain && (
                    <Box
                      sx={{
                        border: "1px solid rgba(33,146,151,0.2)",
                        borderTop: "none",
                        borderRadius: "0 0 12px 12px",
                        overflow: "hidden",
                      }}
                    >
                      <PrioritySlider value={parentPriority} onChange={handleParentPriorityChange} />
                    </Box>
                  )}
                </Grid>

                <Grid
                  item xs={12} md={2}
                  display="flex" alignItems="center" justifyContent="center"
                  sx={{ pt: { md: "80px !important" } }}
                >
                  <Box textAlign="center">
                    <Box
                      sx={{
                        width: 40, height: 40,
                        borderRadius: "50%",
                        mx: "auto",
                        background: parentDomain && childDomains.length
                          ? "linear-gradient(135deg, #219297 0%, #1a7579 100%)"
                          : "#f0f4f4",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.25s ease",
                        boxShadow: parentDomain && childDomains.length
                          ? "0 4px 14px rgba(33,146,151,0.35)"
                          : "none",
                      }}
                    >
                      <MergeTypeIcon
                        sx={{
                          fontSize: 20,
                          color: parentDomain && childDomains.length ? "#fff" : "#ccc",
                          transition: "color 0.25s",
                        }}
                      />
                    </Box>
                    {parentDomain && childDomains.length > 0 && (
                      <Box mt={0.75}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#fff", fontSize: "10px", fontWeight: 700,
                            backgroundColor: "#219297", borderRadius: "10px",
                            px: 1, py: 0.25, letterSpacing: "0.03em",
                          }}
                        >
                          {childDomains.length}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={5}>
                  <ListPanel
                    title="Child Domains"
                    subtitle="Select one or more"
                    search={rightSearch}
                    onSearch={setRightSearch}
                    items={filteredRight}
                    renderItem={(domain) => {
                      const isParent = parentDomain?.domain_token === domain.domain_token;
                      return (
                        <DomainListItem
                          key={domain.domain_token}
                          domain={domain}
                          isSelected={!!childDomains.find(d => d.domain_token === domain.domain_token)}
                          onClick={() => handleToggleChild(domain)}
                          isDisabled={isParent}
                          showCheckbox
                        />
                      );
                    }}
                  />
                </Grid>
              </Grid>

              {childDomains.length > 0 && (
                <Box
                  mt={2}
                  sx={{
                    border: "1px solid rgba(33,146,151,0.15)",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(33,146,151,0.06)",
                  }}
                >
                  <Box
                    px={2} py={1.25}
                    sx={{
                      borderBottom: "1px solid rgba(33,146,151,0.1)",
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="caption" fontWeight={800}
                        sx={{
                          textTransform: "uppercase", letterSpacing: "0.09em",
                          fontSize: "10px", color: "#219297",
                        }}
                      >
                        Child Domain Priorities
                      </Typography>
                      <Typography variant="caption" color="#9db8b8" fontSize="11px">
                        · {childDomains.length} domain{childDomains.length !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                  </Box>

                  {childDomains.map((d, idx) => (
                    <Box
                      key={d.domain_token}
                      sx={{
                        borderBottom: idx < childDomains.length - 1
                          ? "1px solid rgba(33,146,151,0.07)"
                          : "none",
                        backgroundColor: idx % 2 === 0 ? "#fff" : "#fafefe",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, pt: 1.25 }}>
                        <Box
                          sx={{
                            width: 22, height: 22, borderRadius: "5px",
                            backgroundColor: "rgba(33,146,151,0.08)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <LanguageIcon sx={{ fontSize: 11, color: "#219297" }} />
                        </Box>
                        <Typography
                          variant="caption" fontSize="12.5px" fontWeight={500}
                          color="#344767" noWrap flex={1}
                        >
                          {d.domain_name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleChild(d)}
                          sx={{
                            p: 0.5, color: "#ccc", borderRadius: "6px",
                            "&:hover": { color: "#e74c3c", backgroundColor: "rgba(231,76,60,0.08)" },
                            transition: "all 0.15s",
                          }}
                        >
                          <CloseOutlined sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Box>
                      <PrioritySlider
                        value={childPriorities[d.domain_token] ?? 0}
                        onChange={(v) => handleChildPriorityChange(d.domain_token, v)}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>

        <Box
          px={3} py={2}
          sx={{
            borderTop: "1px solid #edf4f4",
            background: "linear-gradient(135deg, #fafefe 0%, #f5fbfb 100%)",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}
        >
          <Box>
            {priorityTotal !== null && !totalIsValid && (
              <Box display="flex" alignItems="center" gap={0.75}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#e74c3c" }} />
                <Typography variant="caption" color="#c0392b" fontSize="12px" fontWeight={500}>
                  Priorities must sum to 100 — currently {priorityTotal}
                </Typography>
              </Box>
            )}
          </Box>

          <Button
            variant="contained"
            size="small"
            startIcon={
              merging
                ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                : <MergeTypeIcon sx={{ fontSize: "15px !important" }} />
            }
            onClick={handleMerge}
            disabled={merging || !parentDomain || childDomains.length === 0 || !totalIsValid}
            sx={{
              background: "linear-gradient(135deg, #219297 0%, #1a7579 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #1d8388 0%, #156568 100%)",
                boxShadow: "0 4px 14px rgba(33,146,151,0.4)",
                transform: "translateY(-1px)",
              },
              "&:active": { transform: "translateY(0)" },
              "&:disabled": { background: "#eaeaea", color: "#bbb", boxShadow: "none", transform: "none" },
              color: "#fff", fontSize: "13px", fontWeight: 600,
              textTransform: "none", px: 2.5, py: 0.85,
              boxShadow: "0 2px 8px rgba(33,146,151,0.25)",
              borderRadius: "9px", letterSpacing: "0.01em",
              transition: "all 0.2s ease",
            }}
          >
            {merging ? "Merging…" : "Merge Domains"}
          </Button>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default MergeDomainsSection;